import { flushUsageEvents, trackEvent } from './usageEvents';

/**
 * Общий слушатель контролов: читает data-ev с элемента, на котором произошло действие.
 *
 * Слушатель висит в capture-фазе на document, поэтому видит порталы Radix и обработчики со
 * stopPropagation. Какое событие считать действием, решают эти функции: иначе нативный select
 * и checkbox дали бы и click, и change, а щелчок в поле ввода считался бы действием.
 * Текст поля не читается никогда.
 */
const FORM_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);
// Их значение сообщает обёртка по onValueChange (ui/tabs, ui/select, ui/radio-group), а открытие
// списка не действие.
const VALUE_REPORTING_ROLES = new Set(['tab', 'combobox', 'radio']);
const TOGGLE_ROLES = new Set(['switch', 'checkbox']);
const NATIVE_TOGGLE_TYPES = new Set(['checkbox', 'radio']);

const toEvent = (name, value) => (value ? { name, value } : { name });

export const resolveClickEvent = (target) => {
  const control = target?.closest?.('[data-ev]');

  if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true') {
    return null;
  }

  const role = control.getAttribute('role');

  if (FORM_TAGS.has(control.tagName) || control.tagName === 'AUDIO' || VALUE_REPORTING_ROLES.has(role)) {
    return null;
  }

  if (TOGGLE_ROLES.has(role)) {
    // Capture идёт раньше обработчика React: aria-checked ещё прежний, значит новое — обратное.
    return toEvent(control.dataset.ev, control.getAttribute('aria-checked') === 'true' ? 'false' : 'true');
  }

  return toEvent(control.dataset.ev, control.dataset.evValue);
};

export const resolveChangeEvent = (target) => {
  if (!FORM_TAGS.has(target?.tagName) || !target.dataset.ev) {
    return null;
  }

  if (target.tagName === 'INPUT' && NATIVE_TOGGLE_TYPES.has(target.type)) {
    return toEvent(target.dataset.ev, String(target.checked));
  }

  if (target.tagName === 'SELECT' && target.hasAttribute('data-ev-send-value')) {
    return toEvent(target.dataset.ev, target.value);
  }

  return toEvent(target.dataset.ev, target.dataset.evValue);
};

export const resolveMediaEvent = (target, action) =>
  target?.tagName === 'AUDIO' && target.dataset.ev ? toEvent(`${target.dataset.ev}.${action}`) : null;

const report = (event) => {
  if (event) {
    trackEvent(event.name, event.value);
  }
};

/** @returns {() => void} снимает слушатели */
export const startUsageCapture = () => {
  const onClick = (event) => report(resolveClickEvent(event.target));
  const onChange = (event) => report(resolveChangeEvent(event.target));
  const onPlay = (event) => report(resolveMediaEvent(event.target, 'play'));
  const onPause = (event) => report(resolveMediaEvent(event.target, 'pause'));
  const onHidden = () => {
    if (document.visibilityState === 'hidden') {
      flushUsageEvents();
    }
  };

  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onChange, true);
  // Медиа-события не всплывают, но capture-фазу проходят.
  document.addEventListener('play', onPlay, true);
  document.addEventListener('pause', onPause, true);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', flushUsageEvents);

  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('change', onChange, true);
    document.removeEventListener('play', onPlay, true);
    document.removeEventListener('pause', onPause, true);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', flushUsageEvents);
  };
};
